import { useRef, useEffect, useState } from "react";
import * as THREE from "three";
import cabezaUrl from "../assets/robot-head.png";
import { RoundedBoxGeometry } from "three/examples/jsm/geometries/RoundedBoxGeometry.js";
import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer.js";
import { RenderPass } from "three/examples/jsm/postprocessing/RenderPass.js";
import { UnrealBloomPass } from "three/examples/jsm/postprocessing/UnrealBloomPass.js";
import { OutputPass } from "three/examples/jsm/postprocessing/OutputPass.js";

/**
 * La cabeza del robot en 3D DE VERDAD: modelo hecho en codigo con Three.js —gira y se ven los
 * lados—, centrado en la portada y siguiendo al puntero por toda la pagina (el gesto del zorro de
 * MetaMask). No es un GLB: es geometria (cuerpo oscuro, borde naranja, pantalla con ojos verdes,
 * orejas y antenas) con bloom para el neon HD. Todo se compila en el bundle: cero CDN, cero red.
 *
 * Cuando exista `robot-head.glb` (tu render pasado a 3D) sera un cambio de una linea: cargarlo con
 * GLTFLoader en vez de construir la geometria. Hasta entonces, esta version estilizada da el 3D real.
 */
/** ¿Hay WebGL de verdad? En equipos sin aceleración o con la GPU bloqueada, no — y entonces el 3D
 *  se cae a la imagen del robot en vez de dejar un hueco vacío. */
function hayWebGL() {
  try {
    const c = document.createElement("canvas");
    return !!(window.WebGLRenderingContext && (c.getContext("webgl") || c.getContext("experimental-webgl")));
  } catch {
    return false;
  }
}

export function RobotHead3D() {
  const mount = useRef<HTMLDivElement>(null);
  const [fallo, setFallo] = useState(!hayWebGL());

  useEffect(() => {
    if (fallo) return;
    const el = mount.current;
    if (!el) return;
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    try {
    let W = el.clientWidth || 440;
    let H = el.clientHeight || 380;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(35, W / H, 0.1, 100);
    camera.position.set(0, 0, 7.4);

    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.setSize(W, H);
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.0;
    el.appendChild(renderer.domElement);

    // ---- materiales -----------------------------------------------------------
    // Colores calcados de la referencia: cuerpo azul-indigo MUY oscuro (no gris), borde naranja,
    // ojos verde vivo saturado. La pantalla, casi negra azulada.
    const cuerpo = new THREE.MeshStandardMaterial({ color: 0x342d50, roughness: 0.6, metalness: 0.2 });
    const oscuro = new THREE.MeshStandardMaterial({ color: 0x080a14, roughness: 0.32, metalness: 0.2 });
    const verde = new THREE.MeshStandardMaterial({ color: 0x0a2a16, emissive: 0x24f24e, emissiveIntensity: 0.72, roughness: 0.3 });
    const verdeLinea = new THREE.MeshStandardMaterial({ color: 0x0a2a16, emissive: 0x24f24e, emissiveIntensity: 0.5, roughness: 0.35 });
    const naranja = new THREE.MeshStandardMaterial({ color: 0x1a0a04, emissive: 0xff6a24, emissiveIntensity: 1.2, roughness: 0.4 });

    const robot = new THREE.Group();

    const head = new THREE.Mesh(new RoundedBoxGeometry(3.2, 2.55, 2.0, 6, 0.62), cuerpo);
    robot.add(head);

    const screen = new THREE.Mesh(new RoundedBoxGeometry(2.35, 1.75, 0.35, 5, 0.26), oscuro);
    screen.position.set(0, 0.06, 0.98);
    robot.add(screen);

    // Nada de marco naranja rodeando la pantalla: en la referencia el bisel es del mismo purpura
    // oscuro. El naranja es piping FINO — una linea sutil en la costura superior del panel y otra
    // bajo el menton, mas las orejas/antenas de abajo.
    const linea = new THREE.BoxGeometry(1.9, 0.035, 0.08);
    const ceja = new THREE.Mesh(linea, naranja); ceja.position.set(0, 1.02, 1.0); robot.add(ceja);
    const menton = new THREE.Mesh(new THREE.BoxGeometry(1.3, 0.035, 0.08), naranja);
    menton.position.set(0, -1.16, 0.95); robot.add(menton);

    // ojos: capsulas verticales (ovalos) emisivos
    const ojoGeo = new THREE.CapsuleGeometry(0.3, 0.34, 8, 20);
    const ojoL = new THREE.Mesh(ojoGeo, verde); ojoL.position.set(-0.52, 0.06, 1.2); robot.add(ojoL);
    const ojoR = ojoL.clone(); ojoR.position.x = 0.52; robot.add(ojoR);

    // corchetes verdes en las 4 esquinas de la pantalla (los marcos de "enfoque" de la referencia)
    const hGeo = new THREE.BoxGeometry(0.34, 0.055, 0.05);
    const vGeo = new THREE.BoxGeometry(0.055, 0.34, 0.05);
    for (const sx of [-1, 1]) for (const sy of [-1, 1]) {
      const cx = sx * 0.92, cy = 0.06 + sy * 0.66;
      const bh = new THREE.Mesh(hGeo, verdeLinea); bh.position.set(cx - sx * 0.14, cy, 1.18); robot.add(bh);
      const bv = new THREE.Mesh(vGeo, verdeLinea); bv.position.set(cx, cy - sy * 0.14, 1.18); robot.add(bv);
    }

    // orejas laterales con tira naranja
    const orejaGeo = new RoundedBoxGeometry(0.42, 0.95, 0.95, 4, 0.22);
    const orejaL = new THREE.Mesh(orejaGeo, cuerpo); orejaL.position.set(-1.75, 0, 0); robot.add(orejaL);
    const orejaR = orejaL.clone(); orejaR.position.x = 1.75; robot.add(orejaR);
    const tiraL = new THREE.Mesh(new THREE.BoxGeometry(0.07, 0.55, 0.14), naranja); tiraL.position.set(-1.99, 0, 0.4); robot.add(tiraL);
    const tiraR = tiraL.clone(); tiraR.position.x = 1.99; robot.add(tiraR);

    // antenas con punta naranja
    const antGeo = new THREE.CapsuleGeometry(0.09, 0.85, 6, 12);
    const antL = new THREE.Mesh(antGeo, cuerpo); antL.position.set(-1.05, 1.65, -0.15); antL.rotation.z = 0.32; robot.add(antL);
    const antR = antL.clone(); antR.position.x = 1.05; antR.rotation.z = -0.32; robot.add(antR);
    const puntaL = new THREE.Mesh(new THREE.SphereGeometry(0.15, 20, 20), cuerpo); puntaL.position.set(-1.33, 2.08, -0.15); robot.add(puntaL);
    const puntaR = puntaL.clone(); puntaR.position.x = 1.33; robot.add(puntaR);
    // pequeño anillo naranja en la base de cada antena (como la referencia)
    const baseAnt = new THREE.TorusGeometry(0.12, 0.03, 10, 20);
    const baL = new THREE.Mesh(baseAnt, naranja); baL.position.set(-1.05, 1.2, -0.05); baL.rotation.x = Math.PI / 2; robot.add(baL);
    const baR = baL.clone(); baR.position.x = 1.05; robot.add(baR);

    // insignia "K" en la frente (disco naranja)
    const disco = new THREE.Mesh(new THREE.TorusGeometry(0.2, 0.045, 12, 28), naranja);
    disco.position.set(0, 1.02, 1.02); robot.add(disco);

    scene.add(robot);

    // ---- luces ----------------------------------------------------------------
    // Iluminacion PAREJA: se ve todo el cuerpo azul-indigo (como en la referencia), con toque
    // naranja en ambos lados y un relleno frontal suave; nada de medio robot en sombra negra.
    scene.add(new THREE.AmbientLight(0xd2ccdf, 0.72));
    const key = new THREE.DirectionalLight(0xfff3ea, 1.35); key.position.set(2, 3, 5); scene.add(key);
    const front = new THREE.DirectionalLight(0xdfe6ff, 0.6); front.position.set(0, 0.5, 6); scene.add(front);
    const rimL = new THREE.PointLight(0xff6a2b, 34, 30, 2); rimL.position.set(-3.6, 1.4, -1.5); scene.add(rimL);
    const rimR = new THREE.PointLight(0xff7a30, 26, 30, 2); rimR.position.set(3.6, 1.2, -1.5); scene.add(rimR);
    const fill = new THREE.PointLight(0x6ab0ff, 12, 30, 2); fill.position.set(2.5, -2.5, 3); scene.add(fill);

    // ---- bloom (neon HD) ------------------------------------------------------
    const composer = new EffectComposer(renderer);
    composer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    composer.setSize(W, H);
    composer.addPass(new RenderPass(scene, camera));
    const bloom = new UnrealBloomPass(new THREE.Vector2(W, H), 0.32, 0.4, 0.92);
    composer.addPass(bloom);
    composer.addPass(new OutputPass());

    // ---- interaccion: seguir al puntero en toda la ventana --------------------
    let tgtY = 0, tgtX = 0;
    const onMove = (e: PointerEvent) => {
      const nx = (e.clientX / window.innerWidth) * 2 - 1;
      const ny = (e.clientY / window.innerHeight) * 2 - 1;
      tgtY = nx * 0.7;
      tgtX = ny * 0.45;
    };
    if (!reduce) window.addEventListener("pointermove", onMove, { passive: true });

    const clock = new THREE.Clock();
    let raf = 0;
    const loop = () => {
      raf = requestAnimationFrame(loop);
      const t = clock.getElapsedTime();
      robot.rotation.y += (tgtY - robot.rotation.y) * 0.06;
      robot.rotation.x += (tgtX - robot.rotation.x) * 0.06;
      if (!reduce) robot.position.y = Math.sin(t * 1.1) * 0.09;
      composer.render();
    };
    loop();

    const ro = new ResizeObserver(() => {
      W = el.clientWidth || W; H = el.clientHeight || H;
      camera.aspect = W / H; camera.updateProjectionMatrix();
      renderer.setSize(W, H); composer.setSize(W, H);
    });
    ro.observe(el);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("pointermove", onMove);
      ro.disconnect();
      composer.dispose();
      renderer.dispose();
      scene.traverse((o) => {
        const m = o as THREE.Mesh;
        if (m.geometry) m.geometry.dispose();
      });
      if (renderer.domElement.parentNode === el) el.removeChild(renderer.domElement);
    };
    } catch {
      // WebGL falló a mitad de montar: nos caemos a la imagen en vez de dejar el hueco vacío.
      setFallo(true);
    }
  }, [fallo]);

  if (fallo) {
    return (
      <div className="robot3d robot3d-img" aria-hidden
        style={{ backgroundImage: `url(${cabezaUrl})` }} />
    );
  }
  return <div className="robot3d" ref={mount} aria-hidden />;
}
